import React, { Component, RefObject } from "react";
import "./style.scss";

interface CursorProps {}

const SETTLE_THRESHOLD_PX = 0.1;

class Cursor extends Component<CursorProps> {
  cursor: RefObject<HTMLDivElement>;
  cursorTrailing: RefObject<HTMLDivElement>;
  animationFrame: number | null;

  mouseX: number;
  mouseY: number;
  trailingX: number;
  trailingY: number;

  cursorText: string;
  cursorMode: string;

  constructor(props: CursorProps) {
    super(props);

    this.cursor = React.createRef();
    this.cursorTrailing = React.createRef();
    this.animationFrame = null;

    this.mouseX = 0;
    this.mouseY = 0;
    this.trailingX = 0;
    this.trailingY = 0;

    this.cursorText = "";
    this.cursorMode = "";
  }

  componentDidMount() {
    if (isTouchDevice()) return;
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mousemove", this.onMouseHover);
  }

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mousemove", this.onMouseHover);
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  onMouseHover = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target || typeof target.closest !== "function") return;
    if (target.closest(".no-custom-cursor")) return;

    this.cursorText = "";
    this.cursorMode = "";

    const artistProfile = target.closest(".artist-profile-play") as HTMLElement | null;
    if (artistProfile) {
      const hasTracks = artistProfile.dataset.hasTracks === "true";
      const isCurrentlyPlaying = artistProfile.dataset.playing === "true";
      if (hasTracks) {
        this.cursorText = isCurrentlyPlaying ? "PAUSE" : "PLAY";
        this.cursorMode = "play";
      }
    } else if (target.closest(".music-right")) {
      this.cursorText = "DRAG";
      this.cursorMode = "drag";
    } else if (target.closest(".album, .album-inner, .album-artwork, .featured-song")) {
      this.cursorText = "VIEW";
      this.cursorMode = "view";
    } else if (target.closest(".FeaturedVideo, .VideoGrid, .VideoContent, .youtube-embed")) {
      this.cursorText = "PLAY";
      this.cursorMode = "play";
    } else if (target.closest(".Gallery, .ImageGallery")) {
      this.cursorText = "VIEW";
      this.cursorMode = "view";
    } else if (target.closest(".PopularTracks")) {
      this.cursorText = "PLAY";
      this.cursorMode = "play";
    } else if (target.closest("a, button, .MuiChip-root, .MuiIconButton-root, .MuiTab-root, [role='button']")) {
      this.cursorMode = "hover";
    }

    if (this.cursorTrailing.current) {
      this.cursorTrailing.current.setAttribute("data-text", this.cursorText);
      this.cursorTrailing.current.className = `cursor-trailer ${this.cursorMode}`;
    }
  };

  onMouseMove = (evt: MouseEvent) => {
    this.mouseX = evt.clientX;
    this.mouseY = evt.clientY;
    if (this.cursorTrailing.current) {
      this.cursorTrailing.current.style.opacity = "1";
    }
    this.startAnimation();
  };

  startAnimation = () => {
    if (this.animationFrame !== null) return;
    this.animationFrame = requestAnimationFrame(this.moveCursor);
  };

  moveCursor = () => {
    this.animationFrame = null;

    const diffX = this.mouseX - this.trailingX;
    const diffY = this.mouseY - this.trailingY;
    const ease = this.cursorMode === "drag" ? 5 : 6;

    this.trailingX += diffX / ease;
    this.trailingY += diffY / ease;

    if (this.cursor.current) {
      this.cursor.current.style.transform = `translate3d(${this.mouseX - 4}px, ${this.mouseY - 4}px, 0)`;
    }
    if (this.cursorTrailing.current) {
      this.cursorTrailing.current.style.display = "block";
      this.cursorTrailing.current.style.transform = `translate3d(${this.trailingX - 10}px, ${this.trailingY - 10}px, 0)`;
    }

    if (Math.abs(diffX) > SETTLE_THRESHOLD_PX || Math.abs(diffY) > SETTLE_THRESHOLD_PX) {
      this.startAnimation();
    }
  };

  render = () => {
    return (
      <div className="cursors-container">
        <div className="cursor" ref={this.cursor} />
        <div className="cursor-trailer" ref={this.cursorTrailing} data-text="" />
      </div>
    );
  };
}

export default Cursor;

function isTouchDevice(): boolean {
  const prefixes = " -webkit- -moz- -o- -ms- ".split(" ");
  const mq = function (query: string): boolean {
    return window.matchMedia(query).matches;
  };

  if ("ontouchstart" in window || (window as { DocumentTouch?: unknown }).DocumentTouch) {
    return true;
  }

  const query = ["(", prefixes.join("touch-enabled),("), "heartz", ")"].join("");
  return mq(query);
}
